import { ApplicationConfig, importProvidersFrom, provideZoneChangeDetection } from '@angular/core';
import {
    provideRouter,
    RouteReuseStrategy,
    TitleStrategy,
    withInMemoryScrolling,
    withViewTransitions,
} from '@angular/router';

import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { provideClientHydration, withHttpTransferCacheOptions } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { routes } from '@app/app.routes';
import { authInterceptor } from '@core/interceptors/auth.interceptor';
import { baseInterceptor } from '@core/interceptors/base.interceptor';
import { trackingInterceptor } from '@core/interceptors/tracking.interceptor';
import {
    provideImageLoader,
    provideUniversalDeviceDetector,
} from '@core/providers/common.provider';
import { provideSentry } from '@core/providers/sentry.provider';
import { provideSwConfig } from '@core/providers/sw-config.provider';
import { PageTitleStrategy } from '@core/strategies/page-title.strategy';
import CustomRouterReuseStrategy from '@core/strategies/router-reuse.strategy';
import { devToolProviders } from '@devtools/devtools';
import { provideEffects } from '@ngrx/effects';
import { provideStore } from '@ngrx/store';
import { AuthModule } from '@pages/auth/auth.module';
import { provideRouterReuseStrategy, provideTitleStrategy } from '@providers/common.provider';
import { provideHttpService } from '@services/http.service';
import { MasterDataEffects } from '@store/effects/master-data.effect';
import { rootReducers } from '@store/store';
import { provideAngularSvgIcon } from 'angular-svg-icon';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import player from 'lottie-web';
import { provideLottieOptions } from 'ngx-lottie';
import provideSystemConfig from '@providers/system-config.provider';

gsap.registerPlugin(ScrollTrigger);

export const appConfig: ApplicationConfig = {
    providers: [
        provideZoneChangeDetection({ eventCoalescing: true }),
        provideRouter(
            routes,
            withInMemoryScrolling({
                anchorScrolling: 'enabled',
                scrollPositionRestoration: 'enabled',
            }),
            withViewTransitions()
        ),
        provideClientHydration(
            withHttpTransferCacheOptions({
                includePostRequests: true,
            })
        ),
        provideAnimations(),
        provideAnimationsAsync(),
        provideHttpClient(
            withFetch(),
            withInterceptors([baseInterceptor, authInterceptor, trackingInterceptor])
        ),
        provideHttpService(),
        provideUniversalDeviceDetector(),
        provideTitleStrategy(),
        provideRouterReuseStrategy(),
        provideAngularSvgIcon(),
        provideLottieOptions({
            player: () => player,
        }),
        provideStore(rootReducers),
        provideEffects([MasterDataEffects]),
        importProvidersFrom(AuthModule.forRoot()),
        provideSentry(),
        provideImageLoader(),
        provideSwConfig(),
        provideSystemConfig(),
        ...devToolProviders,
    ],
};
